"use client";

// [ERP-FLOATING-CHAT-MEMO-VISIBILITY-PER-DEVICE-DEV-20260629-001]
// 전역 플로팅 위젯 설정 버튼(좌하단 작은 ⚙️). 채팅/메모 위젯을 숨겨도 여기서 다시 켤 수 있는
// 항상-접근가능한 설정 진입점. 채팅/메모 표시 ON/OFF + 위치 선택 + 전체 기본값 초기화.
// localStorage 기반(기기별), DB 저장 없음.

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import {
  FLOATING_POSITION_OPTIONS,
  FLOATING_SETTINGS_EVENT,
  type FloatingPosition,
  type FloatingWidget,
  loadFloatingPosition,
  loadFloatingVisible,
  resetAllFloatingSettings,
  saveFloatingPosition,
  saveFloatingVisible,
} from "@/lib/floatingPosition";

function hasToken(): boolean {
  if (typeof window === "undefined") return false;
  return !!localStorage.getItem("access_token");
}

export default function FloatingSettingsButton() {
  const pathname = usePathname();

  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [open, setOpen] = useState(false);

  const [chatVisible, setChatVisible] = useState(true);
  const [memoVisible, setMemoVisible] = useState(true);
  const [chatPos, setChatPos] = useState<FloatingPosition>("bottom-right");
  const [memoPos, setMemoPos] = useState<FloatingPosition>("bottom-right");

  const wrapRef = useRef<HTMLDivElement | null>(null);

  const syncFromStorage = useCallback(() => {
    setChatVisible(loadFloatingVisible("chat"));
    setMemoVisible(loadFloatingVisible("memo"));
    setChatPos(loadFloatingPosition("chat"));
    setMemoPos(loadFloatingPosition("memo"));
  }, []);

  useEffect(() => {
    setMounted(true);
    if (typeof window === "undefined") return;
    setAuthed(hasToken());
    syncFromStorage();
    // 위젯 내부 ⚙️ 등에서 설정이 바뀌면 동기화.
    const onChanged = () => syncFromStorage();
    window.addEventListener(FLOATING_SETTINGS_EVENT, onChanged);
    window.addEventListener("storage", onChanged);
    return () => {
      window.removeEventListener(FLOATING_SETTINGS_EVENT, onChanged);
      window.removeEventListener("storage", onChanged);
    };
  }, [syncFromStorage]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setAuthed(hasToken());
  }, [pathname]);

  // 바깥 클릭 닫기
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const setVisible = useCallback((widget: FloatingWidget, visible: boolean) => {
    saveFloatingVisible(widget, visible);
    if (widget === "chat") setChatVisible(visible);
    else setMemoVisible(visible);
  }, []);

  const setPos = useCallback((widget: FloatingWidget, pos: FloatingPosition) => {
    saveFloatingPosition(widget, pos);
    if (widget === "chat") setChatPos(pos);
    else setMemoPos(pos);
  }, []);

  const handleResetAll = useCallback(() => {
    resetAllFloatingSettings();
    syncFromStorage();
  }, [syncFromStorage]);

  // 위젯과 동일한 숨김 정책(모바일/POS/비인증)에서는 설정 버튼도 숨김.
  const isMobile = pathname === "/mobile" || pathname?.startsWith("/mobile/");
  const isPos = pathname === "/store" || pathname?.startsWith("/store/");
  // [PURCHASE-003 Phase 1] iPad 전용 화면(하단 고정 버튼)에서도 숨김.
  const isTablet = pathname === "/tablet" || pathname?.startsWith("/tablet/");
  if (!mounted || !authed || isMobile || isPos || isTablet) return null;

  return (
    <div ref={wrapRef} className="fixed z-[140] bottom-2 left-2">
      <button
        type="button"
        title="플로팅 위젯 설정"
        aria-label="플로팅 위젯 설정"
        onClick={() => setOpen((v) => !v)}
        className="w-9 h-9 rounded-full bg-white/90 hover:bg-white border border-gray-300 shadow text-gray-500 hover:text-gray-800 flex items-center justify-center text-base"
      >
        ⚙️
      </button>

      {open && (
        <div
          className="absolute bottom-11 left-0 w-64 bg-white rounded-xl shadow-2xl border border-gray-200 p-3"
          role="dialog"
          aria-label="플로팅 위젯 설정"
        >
          <div className="text-xs font-bold text-gray-700 mb-2">플로팅 위젯 설정</div>

          {/* 채팅 */}
          <div className="mb-3">
            <label className="flex items-center gap-2 text-sm text-gray-800 mb-1">
              <input
                type="checkbox"
                checked={chatVisible}
                onChange={(e) => setVisible("chat", e.target.checked)}
              />
              💬 채팅 표시
            </label>
            <select
              value={chatPos}
              disabled={!chatVisible}
              onChange={(e) => setPos("chat", e.target.value as FloatingPosition)}
              className="w-full text-xs border border-gray-200 rounded px-2 py-1 disabled:bg-gray-100 disabled:text-gray-400"
            >
              {FLOATING_POSITION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>채팅 위치: {o.label}</option>
              ))}
            </select>
          </div>

          {/* 메모 */}
          <div className="mb-3">
            <label className="flex items-center gap-2 text-sm text-gray-800 mb-1">
              <input
                type="checkbox"
                checked={memoVisible}
                onChange={(e) => setVisible("memo", e.target.checked)}
              />
              📝 메모 표시
            </label>
            <select
              value={memoPos}
              disabled={!memoVisible}
              onChange={(e) => setPos("memo", e.target.value as FloatingPosition)}
              className="w-full text-xs border border-gray-200 rounded px-2 py-1 disabled:bg-gray-100 disabled:text-gray-400"
            >
              {FLOATING_POSITION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>메모 위치: {o.label}</option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={handleResetAll}
            className="w-full text-xs text-gray-600 border border-gray-200 rounded px-2 py-1.5 hover:bg-gray-50"
          >
            ↺ 전체 기본값으로 초기화
          </button>
        </div>
      )}
    </div>
  );
}
